import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';

export const client = createClient({
  projectId: process.env.REACT_APP_SANITY_PROJECT_ID || '7d3wror3',
  dataset: process.env.REACT_APP_SANITY_DATASET || 'production',
  apiVersion: '2024-01-15', // Use today's date or your preferred version
  useCdn: true, // Set to false if you want to ensure fresh data
  // token: process.env.REACT_APP_SANITY_TOKEN, // Commented out for testing without token
});

// Image builder for Sanity images
const builder = imageUrlBuilder(client);

export const urlFor = (source: any) => {
  return builder.image(source);
};

// Blog post query
export const getBlogPosts = async () => {
  const query = `*[_type == "post"] | order(publishedAt desc) {
    _id,
    title,
    slug,
    excerpt,
    publishedAt,
    "author": author->name,
    "authorImage": author->image,
    "authorSlug": author->slug,
    "mainImage": mainImage.asset->url,
    "categories": categories[]->title,
    "categorySlugs": categories[]->slug,
    "tags": tags[]->title,
    "tagSlugs": tags[]->slug,
    "seo": {
      "metaTitle": seo.metaTitle,
      "metaDescription": seo.metaDescription,
      "keywords": seo.keywords
    },
    featured,
    body
  }`;
  
  return await (client as any).fetch(query);
};

// Single blog post query
export const getBlogPost = async (slug: string) => {
  const query = `*[_type == "post" && slug.current == $slug][0] {
    _id,
    title,
    slug,
    excerpt,
    publishedAt,
    "author": author->name,
    "authorImage": author->image,
    "authorSlug": author->slug,
    "authorBio": author->bio,
    "authorSocial": author->social,
    "mainImage": mainImage.asset->url,
    "categories": categories[]->title,
    "categorySlugs": categories[]->slug,
    "tags": tags[]->title,
    "tagSlugs": tags[]->slug,
    "seo": {
      "metaTitle": seo.metaTitle,
      "metaDescription": seo.metaDescription,
      "keywords": seo.keywords
    },
    featured,
    body
  }`;
  
  return await (client as any).fetch(query, { slug });
};

// Featured blog posts query
export const getFeaturedPosts = async (limit: number = 3) => {
  const query = `*[_type == "post" && featured == true] | order(publishedAt desc)[0...$limit] {
    _id,
    title,
    slug,
    excerpt,
    publishedAt,
    "author": author->name,
    "authorImage": author->image,
    "mainImage": mainImage.asset->url,
    "categories": categories[]->title,
    "tags": tags[]->title,
    featured
  }`;
  
  return await (client as any).fetch(query, { limit });
};

// Blog posts by category
export const getPostsByCategory = async (category: string) => {
  const query = `*[_type == "post" && $category in categories[]->slug.current] | order(publishedAt desc) {
    _id,
    title,
    slug,
    excerpt,
    publishedAt,
    "author": author->name,
    "authorImage": author->image,
    "mainImage": mainImage.asset->url,
    "categories": categories[]->title,
    "tags": tags[]->title,
    featured
  }`;
  
  return await (client as any).fetch(query, { category });
};

// Blog posts by tag
export const getPostsByTag = async (tag: string) => {
  const query = `*[_type == "post" && $tag in tags[]->slug.current] | order(publishedAt desc) {
    _id,
    title,
    slug,
    excerpt,
    publishedAt,
    "author": author->name,
    "authorImage": author->image,
    "mainImage": mainImage.asset->url,
    "categories": categories[]->title,
    "tags": tags[]->title,
    featured
  }`;
  
  return await (client as any).fetch(query, { tag });
};

// Categories query
export const getCategories = async () => {
  const query = `*[_type == "category"] | order(title asc) {
    _id,
    title,
    slug,
    description,
    color
  }`;
  
  return await (client as any).fetch(query);
};

// Tags query
export const getTags = async () => {
  const query = `*[_type == "tag"] | order(title asc) {
    _id,
    title,
    slug,
    description,
    color
  }`;
  
  return await (client as any).fetch(query);
};

// Author query
export const getAuthor = async (slug: string) => {
  const query = `*[_type == "author" && slug.current == $slug][0] {
    _id,
    name,
    slug,
    image,
    bio,
    social
  }`;
  
  return await (client as any).fetch(query, { slug });
};

// Related posts query
export const getRelatedPosts = async (currentPostId: string, categorySlugs: string[], tagSlugs: string[], limit: number = 3) => {
  const query = `*[_type == "post" && _id != $currentPostId && (
    count(categories[]->slug.current[@ in $categorySlugs]) > 0 ||
    count(tags[]->slug.current[@ in $tagSlugs]) > 0
  )] | order(publishedAt desc)[0...$limit] {
    _id,
    title,
    slug,
    excerpt,
    publishedAt,
    "author": author->name,
    "authorImage": author->image,
    "mainImage": mainImage.asset->url,
    "categories": categories[]->title,
    "tags": tags[]->title
  }`;
  
  return await (client as any).fetch(query, { currentPostId, categorySlugs, tagSlugs, limit });
}; 